/**
 * FicheDevisSection — vague 4c : le parcours devis DANS la fiche (item 3.13).
 *
 * Greffé sur la fiche du service technique sans toucher à ses statuts
 * historiques (collision n°3) : les deux vies sont indépendantes. Sans
 * parcours : un bouton pour en lancer un depuis les circuits de la commune.
 * Avec : l'étape courante, les boutons du parcours (montant à l'acceptation,
 * date à la datation, PDF au téléversement) et l'historique complet.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException, type WorkflowInstance } from '@/lib/api';
import { Card, Button, Badge, Alert } from '@/components/ui';
import { WorkflowEnvoisSection } from '@/components/WorkflowEnvoisSection';
import { GitBranch, Paperclip, AlertCircle, Loader2 } from 'lucide-react';
import { CircuitFrise } from '@/components/circuits/CircuitFrise';

const CHAMP = 'h-9 rounded-r-md border border-border bg-surface/60 px-2.5 text-sm text-ink focus:border-accent focus:outline-none';

function dateFr(d: string): string {
  return new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'medium' })
    .format(new Date(d.length === 10 ? `${d}T12:00:00Z` : d));
}

export function FicheDevisSection({ signalementId }: { signalementId: string }) {
  const [instance, setInstance] = useState<WorkflowInstance | null>(null);
  const [historique, setHistorique] = useState<Awaited<ReturnType<typeof api.workflows.historique>>['evenements']>([]);
  const [circuits, setCircuits] = useState<Array<{ id: string; libelle: string; presetSource: string | null }>>([]);
  const [circuitChoisi, setCircuitChoisi] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Saisies contextuelles par transition (montant / date / commentaire)
  const [montant, setMontant] = useState('');
  const [dateCle, setDateCle] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [secteurBudget, setSecteurBudget] = useState('');
  const [secteursDispo, setSecteursDispo] = useState<string[]>([]);

  // M9 — ouvrir une pièce d'étape / réintégrer un document signé à la main.
  const [erreurPiece, setErreurPiece] = useState<string | null>(null);
  async function ouvrirPiece(evenementId: string, quelle: 'piece' | 'signe') {
    if (!instance) return;
    try {
      const { url } = await api.workflows.lienPiece(instance.id, evenementId, quelle);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setErreurPiece(e instanceof Error ? e.message : 'Document indisponible');
    }
  }
  async function reintegrer(evenementId: string, f: File) {
    if (!instance) return;
    setErreurPiece(null);
    try {
      await api.workflows.reintegrerDocumentSigne(instance.id, evenementId, f);
      await charger();
    } catch (e) {
      setErreurPiece(e instanceof Error ? e.message : 'Envoi du document impossible');
    }
  }

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.workflows.instancePourEntite('signalement', signalementId);
      setInstance(r.instance);
      if (r.instance) {
        const h = await api.workflows.historique(r.instance.id);
        setHistorique(h.evenements);
      } else {
        // Pas de parcours : la liste allégée des circuits actifs, accessible
        // au staff (3.13 : la demande est un geste de manager).
        const d = await api.workflows.circuitsActifs().catch(() => ({ circuits: [] }));
        setCircuits(d.circuits);
        const devis = d.circuits.find((x) => x.presetSource === 'devis_travaux');
        if (devis) setCircuitChoisi(devis.id);
      }
    } catch {
      setInstance(null);
    } finally { setLoading(false); }
  }, [signalementId]);

  useEffect(() => { void charger(); }, [charger]);

  // 3.14 — les enveloppes existantes, pour imputer le montant à l'acceptation.
  useEffect(() => {
    api.budgets.suivi()
      .then((b) => setSecteursDispo(b.secteurs.map((s) => s.secteur)))
      .catch(() => setSecteursDispo([]));
  }, []);

  async function lancer() {
    setError(null);
    try {
      await api.workflows.demarrer(circuitChoisi, 'signalement', signalementId);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Lancement impossible');
    }
  }

  async function franchir(transitionId: string, piece?: File) {
    setError(null);
    try {
      await api.workflows.transiterInstance(instance!.id, transitionId, {
        commentaire: commentaire.trim() || undefined,
        dateCle: dateCle || undefined,
        montant: montant !== '' ? Number(montant.replace(',', '.')) : undefined,
        secteurBudget: secteurBudget || undefined,
        piece,
      });
      setMontant(''); setDateCle(''); setCommentaire(''); setSecteurBudget('');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Action impossible');
    }
  }

  if (loading) {
    return (
      <Card variant="glass" padded className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Parcours devis…
      </Card>
    );
  }

  // ── Pas encore de parcours : proposer d'en lancer un ──────────────────────
  if (!instance) {
    if (!circuits.length) return null;   // rien à proposer (ou pas admin)
    return (
      <Card variant="glass" padded>
        <h3 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-ink">
          <GitBranch className="h-4 w-4" aria-hidden="true" /> Parcours devis
        </h3>
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        <div className="flex flex-wrap items-center gap-2">
          <select value={circuitChoisi} onChange={(e) => setCircuitChoisi(e.target.value)}
            aria-label="Circuit à lancer" className={CHAMP}>
            <option value="">— Choisir un circuit —</option>
            {circuits.map((c) => <option key={c.id} value={c.id}>{c.libelle}</option>)}
          </select>
          <Button variant="secondary" size="sm" disabled={!circuitChoisi}
            onClick={() => void lancer()}>
            Lancer le parcours
          </Button>
        </div>
        <p className="mt-2 text-2xs text-ink-muted">
          La demande d'autorisation part à l'encadrement technique dès le
          lancement. Le statut de la fiche n'est pas modifié.
        </p>
      </Card>
    );
  }

  // ── Parcours en cours ──────────────────────────────────────────────────────
  const actions = instance.transitionsDisponibles;
  // Les champs contextuels : montant si une flèche "Accepter" est là,
  // date si une flèche de datation est là — heuristique par libellé du preset,
  // sans rien casser pour un circuit maison (les champs restent facultatifs).
  const montreMontant = actions.some((t) => /accepter/i.test(t.libelle));
  const montreDate = actions.some((t) => /dater|redater|date/i.test(t.libelle));

  return (
    <>
    <Card variant="glass" padded>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-medium text-ink">
          <GitBranch className="h-4 w-4" aria-hidden="true" /> {instance.definitionLibelle}
        </h3>
        <Badge variant={instance.etat.estFinal ? 'success' : 'info'} size="sm">
          {instance.etat.libelle}
        </Badge>
      </div>
      {/* M7 (retour Manon) — la frise du circuit : qui, quand, et la pièce à chaque étape. */}
      {instance.etats && instance.etats.length > 0 && (
        <div className="mb-3">
          <CircuitFrise instanceId={instance.id} etats={instance.etats} etatCourantId={instance.etat.id} historique={historique} />
        </div>
      )}
      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

      {actions.length > 0 && (
        <>
          <div className="mb-2 flex flex-wrap gap-2">
            {montreMontant && (
              <input inputMode="decimal" placeholder="Montant € (à l'acceptation)"
                value={montant} onChange={(e) => setMontant(e.target.value)}
                aria-label="Montant du devis" className={CHAMP} />
            )}
            {montreMontant && secteursDispo.length > 0 && (
              <select value={secteurBudget} onChange={(e) => setSecteurBudget(e.target.value)}
                aria-label="Enveloppe budgétaire imputée" className={CHAMP}>
                <option value="">— Enveloppe (3.14) —</option>
                {secteursDispo.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            )}
            {montreDate && (
              <input type="date" value={dateCle} onChange={(e) => setDateCle(e.target.value)}
                aria-label="Date prévisionnelle" className={CHAMP} />
            )}
            <input placeholder="Commentaire (facultatif)" value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              aria-label="Commentaire" className={`${CHAMP} min-w-40 flex-1`} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {actions.map((t) => (
              t.pieceRequise ? (
                <span key={t.id}>
                  <label htmlFor={`fd-piece-${t.id}`}
                    className={`inline-flex cursor-pointer items-center gap-1.5 rounded-r-md border px-3 py-1.5 text-sm font-medium ${
                      t.autorisee ? 'border-border text-ink hover:border-accent/50' : 'cursor-not-allowed border-border/40 text-ink-muted opacity-50'}`}>
                    <Paperclip className="h-4 w-4" aria-hidden="true" /> {t.libelle}
                  </label>
                  <input id={`fd-piece-${t.id}`} type="file" className="sr-only" disabled={!t.autorisee}
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void franchir(t.id, f);
                      e.target.value = '';
                    }} />
                </span>
              ) : (
                <Button key={t.id} variant="secondary" size="sm" disabled={!t.autorisee}
                  title={t.autorisee ? undefined : 'Réservé à un autre rôle ou service'}
                  onClick={() => void franchir(t.id)}>
                  {t.libelle}
                </Button>
              )
            ))}
          </div>
        </>
      )}

      {/* M13 (retour Manon) : un devis reçu est valable 3 MOIS — l'information
          s'affiche dès qu'une étape « reçu » figure à l'historique, et vire au
          rouge une fois la validité dépassée : un devis périmé peut rallonger
          les délais d'intervention (il faut le redemander). Calcul côté écran
          depuis la date de l'étape : aucun champ de plus à saisir. */}
      {(() => {
        const recu = historique.find((h) => /re[çc]u/i.test(h.versEtat));
        if (!recu) return null;
        const fin = new Date(recu.createdAt);
        fin.setMonth(fin.getMonth() + 3);
        const perime = fin.getTime() < Date.now();
        return (
          <p className={`mt-3 text-xs ${perime ? 'text-urgent' : 'text-ink-muted'}`}>
            {perime
              ? `⚠ Devis périmé depuis le ${dateFr(fin.toISOString())} (validité 3 mois) — à redemander avant lancement.`
              : `Devis valable jusqu'au ${dateFr(fin.toISOString())} (validité usuelle : 3 mois).`}
          </p>
        );
      })()}

      {erreurPiece && <p className="mt-2 text-xs text-urgent">{erreurPiece}</p>}
      {historique.length > 0 && (
        <div className="mt-3 space-y-1 border-t border-border/60 pt-2">
          {historique.map((h) => (
            <p key={h.id} className="text-xs text-ink-muted">
              <span className="text-ink">{dateFr(h.createdAt)}</span>
              {' — '}
              {h.deEtat ? `${h.deEtat} → ` : 'Lancement → '}
              <span className="text-ink">{h.versEtat}</span>
              {h.montant !== null ? ` · ${h.montant.toFixed(2).replace('.', ',')} €` : ''}
              {h.dateCle ? ` · ${dateFr(h.dateCle)}` : ''}
              {h.par ? ` · ${h.par}` : ''}
              {h.commentaire ? ` · « ${h.commentaire} »` : ''}
              {/* M9 (retour Manon) — la pièce s'ouvre enfin (le 📎 n'était
                  pas cliquable), et un document signé à la main par le maire
                  (devis signé…) se réintègre sur l'étape déjà franchie. */}
              {h.aPiece && instance && (
                <button type="button" className="ml-1 text-accent hover:underline"
                  onClick={() => void ouvrirPiece(h.id, 'piece')}>📎 {h.pieceNom ?? 'pièce'}</button>
              )}
              {h.aDocumentSigne && instance && (
                <button type="button" className="ml-1 text-accent hover:underline"
                  title={`Réintégré${h.documentSigneLe ? ` le ${dateFr(h.documentSigneLe)}` : ''}${h.documentSignePar ? ` par ${h.documentSignePar}` : ''}`}
                  onClick={() => void ouvrirPiece(h.id, 'signe')}>✍️ signé</button>
              )}
              {instance && (
                <label className="ml-1 cursor-pointer text-ink-subtle hover:text-accent"
                  title="Réintégrer le scan d'un document signé à la main (ex. devis signé par le maire)">
                  {h.aDocumentSigne ? '· remplacer le signé' : '· joindre le signé'}
                  <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="sr-only"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void reintegrer(h.id, f); e.target.value = ''; }} />
                </label>
              )}
            </p>
          ))}
        </div>
      )}
    </Card>
    {instance && <WorkflowEnvoisSection instanceId={instance.id} />}
    </>
  );
}
